import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types';
import { DESTINATION_TYPE } from '@/app/search/types';
import { SearchModes } from '@/domainLogic/index.ts';
import { isGeoCity, isGeoTag } from '@/app/components/search/Utilities.ts';

// Flight-only has no page of its own here; it keeps the dynamic-package wording, which is the one
// that names a flight.
const resultsTitleKey: { [key in SearchModes]: string } = {
  [SearchModes.HO]: 'vacation.common.page_title_ho_results',
  [SearchModes.DP]: 'vacation.common.page_title_dp_results',
  [SearchModes.TH]: 'vacation.common.page_title_th_results',
  [SearchModes.FF]: 'vacation.common.page_title_dp_results',
};

const detailTitleKey: { [key in SearchModes]: string } = {
  [SearchModes.HO]: 'vacation.common.page_title_ho_detail',
  [SearchModes.DP]: 'vacation.common.page_title_dp_detail',
  [SearchModes.TH]: 'vacation.common.page_title_th_detail',
  [SearchModes.FF]: 'vacation.common.page_title_dp_detail',
};

type CreateResultsTitleProps = {
  destinationName: string | undefined;
  searchMode: SearchModes;
  t: TranslationFunction;
};
/**
 * "Destination - Flight + Hotel Packages": the results page title from the destination name. The
 * document title and the link preview both build it, from different sources for the name.
 */
export const createResultsTitle = ({ destinationName, searchMode, t }: CreateResultsTitleProps): string | undefined =>
  destinationName ? t(resultsTitleKey[searchMode], { destination: destinationName }) : undefined;

type CreateGeneralPageTitleProps = {
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  t: TranslationFunction;
  defaultTitle?: string;
};
export const createGeneralPageTitle = ({
  searchInfoData,
  searchInfoDetailData,
  t,
  defaultTitle = '',
}: CreateGeneralPageTitleProps): string | undefined => {
  const destinationType = searchInfoDetailData?.destination?.destinationType;
  const destinationId = searchInfoDetailData?.destination?.id;
  let destinationName;

  if (
    searchInfoData.searchMode === SearchModes.HO &&
    destinationType === DESTINATION_TYPE.GEO &&
    (isGeoCity(destinationId) || isGeoTag(destinationId))
  ) {
    destinationName = searchInfoDetailData?.destination?.description;
  } else {
    destinationName = searchInfoDetailData?.destination?.cityName;
  }

  return createResultsTitle({ destinationName, searchMode: searchInfoData.searchMode, t }) || defaultTitle;
};

type CreateDetailTitleProps = {
  hotelName: string | undefined;
  destinationName: string | undefined;
  searchMode: SearchModes;
  t: TranslationFunction;
};
/**
 * "Hotel, Destination - Flight + Hotel Package": the detail page title from its two names. The
 * document title and the link preview both build it, from different sources for the names.
 */
export const createDetailTitle = ({
  hotelName,
  destinationName,
  searchMode,
  t,
}: CreateDetailTitleProps): string | undefined => {
  if (hotelName && destinationName) {
    return t(detailTitleKey[searchMode], { hotel_name: hotelName, destination: destinationName });
  }

  return hotelName || undefined;
};

type CreateDetailPageTitleProps = {
  hotelName: string | undefined;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  t: TranslationFunction;
};
export const createDetailPageTitle = ({
  hotelName,
  searchInfoData,
  searchInfoDetailData,
  t,
}: CreateDetailPageTitleProps): string | undefined =>
  createDetailTitle({
    hotelName,
    destinationName: searchInfoDetailData?.destination?.cityName || searchInfoDetailData?.destination?.description,
    searchMode: searchInfoData.searchMode,
    t,
  });

export const setPageTitle = (pageTitle: string | undefined) => {
  if (pageTitle && typeof document !== 'undefined') {
    document.title = pageTitle;
  }
};
